/**
 * The authenticated layout. Everything the Studio does needs an author.
 *
 * While the first ID token resolves there is a quiet line, not a spinner — the Studio does
 * not perform work it isn't doing.
 *
 * **The shell is lazy, and that is what keeps Firestore off the first paint** (`studio/11`).
 * The room — the top bar's meter and "N making", the rail, the floor's "what needs you" — is
 * made of live queries, so a static import would put the whole Firestore SDK in the entry
 * chunk and download it on the sign-in page, where there is nothing yet to read. It is
 * fetched the moment there is a session, which is the moment before the first surface asks
 * for the same module. The wait says exactly what the token's wait says, so a cold load has
 * one quiet line and not two.
 */

import { Wordmark } from '@atlas-nook/design-system';
import { Suspense } from 'react';
import type { JSX } from 'react';
import { Navigate, useLocation } from 'react-router';

import { useT } from '../i18n/I18nProvider.js';
import { lazySurface } from '../routes/Surface.js';
import { useAuth } from './AuthProvider.js';

/**
 * `lazySurface`, not a bare `React.lazy`, for the reason it exists (`routes/Surface.tsx`):
 * once the module is in hand the shell renders **directly**, with no boundary to suspend at
 * — so a navigation inside the app never re-suspends the room, and `preloadSurfaces()` can
 * warm it for the tests exactly as it warms every surface.
 */
export const LazyAppShell = lazySurface(async () => ({
  default: (await import('../shell/AppShell.js')).AppShell,
}));

/**
 * The wait before there is a room (`i-0004`).
 *
 * It cannot show the shell, because the shell is the thing being fetched. What it can do is
 * stop looking like a broken page: it was one unstyled line in the corner of a blank
 * document, which is what a person met on every page load — a bookmark, a reload, a link
 * from an email, the sign-in link landing. Now it stands on the app's own ground and says
 * whose page this is while it opens.
 *
 * Still no spinner and no skeleton pretending to be content — the same refusal the surface
 * kicker makes one level down. A wordmark and a sentence are both simply true.
 */
function Opening(): JSX.Element {
  const t = useT();
  return (
    <div className="opening">
      <Wordmark />
      <p className="note" role="status">
        {t('app.opening')}
      </p>
    </div>
  );
}

export function RequireAuth(): JSX.Element {
  const location = useLocation();
  const { session } = useAuth();

  if (session === undefined) return <Opening />;

  if (session === null) {
    return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  }

  return (
    <Suspense fallback={<Opening />}>
      <LazyAppShell />
    </Suspense>
  );
}
